import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { MarkdownRenderer } from "../Guardian/MarkdownRenderer";
import { 
  Edit2, 
  Trash2, 
  User, 
  MessageSquare,
  MoreHorizontal,
  Clock,
  Activity
} from "lucide-react";
import useAuth from "../../hooks/useAuth";
import {
  listInvestigationComments,
  createInvestigationComment,
  updateInvestigationComment,
  deleteInvestigationComment,
} from "../../clients/metoro/investigations";
import { InvestigationComment } from "../../types/investigation";
import { withErrorHandling } from "../../utility/apiUtils";
import { LoadingSpinner } from "../LoadingSpinner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useToast } from "../ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "../ui/lib/utils";

interface InvestigationCommentsProps {
  investigationUuid: string;
}

const InvestigationComments: React.FC<InvestigationCommentsProps> = ({
  investigationUuid,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [isCommentFocused, setIsCommentFocused] = useState(false);
  const [editingMode, setEditingMode] = useState<"write" | "preview">("write");
  const [newCommentMode, setNewCommentMode] = useState<"write" | "preview">("write");

  const { data, isLoading } = useQuery({
    queryKey: ["investigationComments", investigationUuid],
    queryFn: () =>
      withErrorHandling(() => listInvestigationComments(investigationUuid)),
  });

  const createMutation = useMutation({
    mutationFn: (content: string) =>
      withErrorHandling(() =>
        createInvestigationComment(investigationUuid, { content })
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["investigationComments", investigationUuid],
      });
      setNewComment("");
      setIsCommentFocused(false);
      setNewCommentMode("write");
      toast({
        title: "Comment added",
        description: "Your comment has been added successfully.",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ uuid, content }: { uuid: string; content: string }) =>
      withErrorHandling(() => updateInvestigationComment(uuid, { content })),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["investigationComments", investigationUuid],
      });
      setEditingCommentId(null);
      setEditingContent("");
      toast({
        title: "Comment updated",
        description: "Your comment has been updated successfully.",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) =>
      withErrorHandling(() => deleteInvestigationComment(uuid)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["investigationComments", investigationUuid],
      });
      setDeletingCommentId(null);
      toast({
        title: "Comment deleted",
        description: "The comment has been deleted successfully.",
      });
    },
  });

  const handleCreateComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      createMutation.mutate(newComment.trim());
    }
  };

  const handleUpdateComment = (uuid: string) => {
    if (editingContent.trim()) {
      updateMutation.mutate({ uuid, content: editingContent.trim() });
    }
  };

  const startEditing = (comment: InvestigationComment) => {
    setEditingCommentId(comment.uuid);
    setEditingContent(comment.content);
    setEditingMode("write");
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditingContent("");
    setEditingMode("write");
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const comments = data?.comments || [];

  // Define timeline event types
  type TimelineEvent = {
    id: string;
    type: "created" | "comment";
    user: string;
    timestamp: string;
    content: string;
    updatedAt?: string;
    isComment?: boolean;
    commentData?: InvestigationComment;
  };

  // Mock timeline events (in a real app, these would come from the API)
  const timelineEvents: TimelineEvent[] = [
    {
      id: "created",
      type: "created" as const,
      user: "system",
      timestamp: new Date().toISOString(),
      content: "Investigation created"
    },
    ...comments.map(comment => ({
      id: comment.uuid,
      type: "comment" as const,
      user: comment.creator,
      timestamp: comment.createdAt,
      content: comment.content,
      updatedAt: comment.updatedAt,
      isComment: true,
      commentData: comment
    }))
  ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  return (
    <div className="space-y-4">
      {/* Comments */}
      {comments.map((comment) => (
        <div key={comment.uuid} className="border border-border rounded-lg">
          <div className="px-4 py-3 bg-backgroundmedium border-b border-border rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-backgroundlight flex items-center justify-center">
                  <User className="h-4 w-4 text-textdark" />
                </div>
                <div>
                  <span className="font-medium text-textlight">{comment.creator}</span>
                  <span className="text-textdark ml-2">commented on {format(new Date(comment.createdAt), "MMM d, yyyy")}</span>
                  {comment.updatedAt && comment.updatedAt !== comment.createdAt && (
                    <span className="text-textdark ml-1 italic">· edited</span>
                  )}
                </div>
              </div>
              {comment.creator === user?.email && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-backgroundmedium border-border">
                    <DropdownMenuItem 
                      onClick={() => startEditing(comment)}
                      className="text-textmedium hover:text-textlight"
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuItem 
                      onClick={() => setDeletingCommentId(comment.uuid)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          <div className="p-4">
            {editingCommentId === comment.uuid ? (
              <div className="space-y-2">
                <div className="border border-border rounded-md">
                  <div className="flex border-b border-border">
                    <button
                      className={cn(
                        "px-4 py-2 text-sm font-medium border-r border-border",
                        editingMode === "write" 
                          ? "text-textlight bg-backgrounddark" 
                          : "text-textdark hover:text-textlight"
                      )}
                      onClick={() => setEditingMode("write")}
                    >
                      Write
                    </button>
                    <button
                      className={cn(
                        "px-4 py-2 text-sm font-medium",
                        editingMode === "preview" 
                          ? "text-textlight bg-backgrounddark" 
                          : "text-textdark hover:text-textlight"
                      )}
                      onClick={() => setEditingMode("preview")}
                    >
                      Preview
                    </button>
                  </div>
                  {editingMode === "write" ? (
                    <Textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      className="min-h-[100px] bg-backgrounddark border-0 rounded-none text-textmedium focus:ring-0"
                      placeholder="Leave a comment"
                      autoFocus
                    />
                  ) : (
                    <div className="p-4 min-h-[100px] text-textmedium">
                      <MarkdownRenderer content={editingContent || "*No content*"} />
                    </div>
                  )}
                </div>
                <div className="flex gap-2 justify-end">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={cancelEditing}
                    className="border-border"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleUpdateComment(comment.uuid)}
                    disabled={updateMutation.isPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Update comment
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-textmedium markdown">
                <MarkdownRenderer content={comment.content} />
              </div>
            )}
          </div>
        </div>
      ))}

      {/* New comment form */}
      <div className="border border-border rounded-lg">
        <div className="px-4 py-3 bg-backgroundmedium border-b border-border rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-backgroundlight flex items-center justify-center">
              <User className="h-4 w-4 text-textdark" />
            </div>
            <span className="font-medium text-textlight">{user?.email || "You"}</span>
          </div>
        </div>
        <form onSubmit={handleCreateComment} className="p-4">
          {(isCommentFocused || newComment) ? (
            <div className="space-y-2">
              <div className="border border-border rounded-md">
                <div className="flex border-b border-border">
                  <button
                    type="button"
                    className={cn(
                      "px-4 py-2 text-sm font-medium border-r border-border",
                      newCommentMode === "write" 
                        ? "text-textlight bg-backgrounddark" 
                        : "text-textdark hover:text-textlight"
                    )}
                    onClick={() => setNewCommentMode("write")}
                  >
                    Write
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "px-4 py-2 text-sm font-medium",
                      newCommentMode === "preview" 
                        ? "text-textlight bg-backgrounddark" 
                        : "text-textdark hover:text-textlight"
                    )}
                    onClick={() => setNewCommentMode("preview")}
                  >
                    Preview
                  </button>
                </div>
                {newCommentMode === "write" ? (
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Leave a comment"
                    className="min-h-[100px] bg-backgrounddark border-0 rounded-none text-textmedium placeholder:text-textdark focus:ring-0"
                    autoFocus
                  />
                ) : (
                  <div className="p-4 min-h-[100px] text-textmedium markdown">
                    <MarkdownRenderer content={newComment || "*Nothing to preview*"} />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewComment("");
                    setIsCommentFocused(false);
                    setNewCommentMode("write");
                  }}
                  className="border-border"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!newComment.trim() || createMutation.isPending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {createMutation.isPending ? "Posting..." : "Comment"}
                </Button>
              </div>
            </div>
          ) : (
            <div 
              className="border border-border rounded-md p-3 bg-backgrounddark cursor-text hover:border-primary/50 transition-colors"
              onClick={() => {
                setIsCommentFocused(true);
                setNewCommentMode("write");
              }}
            >
              <p className="text-textdark text-sm">Leave a comment</p>
            </div>
          )}
        </form>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!deletingCommentId}
        onOpenChange={(open) => !open && setDeletingCommentId(null)}
      >
        <AlertDialogContent className="bg-backgroundmedium border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-textlight">Delete Comment</AlertDialogTitle>
            <AlertDialogDescription className="text-textdark">
              Are you sure you want to delete this comment? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-border text-textmedium hover:bg-backgroundlight hover:text-textlight">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingCommentId && deleteMutation.mutate(deletingCommentId)}
              className="bg-red-600 text-white hover:bg-red-700 border-0"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvestigationComments;